'use client';

import { c as _c } from "react/compiler-runtime";
import type { Data } from '@repo/strapi-types';
import { useTranslations } from 'next-intl';
import CkEditorCSRRenderer from '@/components/elementary/CkEditorRenderer/CkEditorCSRRenderer';
import { Container } from '@/components/elementary/Container';
import { Tooltip } from '@/components/elementary/Tooltip';
import { ArrowDown } from '@/components/icons/ArrowDown';
import { Info } from '@/components/icons/Info';
import { BillingDetails } from '@/components/page-builder/global/pricingV1/featuredPlan/BillingDetails';
import { HighlightIcon } from '@/components/page-builder/global/pricingV1/overview/HighlightIcon';
import Heading from '@/components/typography/Heading';
import { Paragraph } from '@/components/typography/Paragraph';
import { FEATURE_COMPARISON_ANCHOR_ID } from '@/constants/pricing.constants';
import { cn } from '@/lib/styles';
import styles from '../featureComparison/FeatureCell.module.css';
export function FeaturedPlan(t0) {
  const $ = _c(40);
  const {
    data
  } = t0;
  const t = useTranslations("Pricing");
  if (!data) {
    return null;
  }
  const {
    title,
    description,
    highlights,
    price,
    priceUnderline,
    button
  } = data;
  let t1;
  if ($[0] !== t) {
    t1 = t("mostPopular");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="p-2 grid place-items-center bg-peach-300 text-brand-black border-x border-t border-neutral-700 rounded-t-lg"><Paragraph fontSize="bodyBoldMedium" className="uppercase">{t1}</Paragraph></div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== title) {
    t3 = <Heading tag="h2" className="w-fit text-neutral-light-100" fontSize="displaySmall">{title}</Heading>;
    $[4] = title;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== description) {
    t4 = <Paragraph fontSize="headingSmallLight" className="text-neutral-light-100">{description}</Paragraph>;
    $[6] = description;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <div className="flex flex-col gap-2.5 pb-6 lg:pb-0">{t3}{t4}</div>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== highlights) {
    t6 = highlights?.slice(0, 5)?.map(_temp);
    $[11] = highlights;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = <div className="flex flex-col gap-4.5 pb-4.5 lg:pb-0">{t6}</div>;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== highlights) {
    t8 = highlights?.slice(5)?.map(_temp2);
    $[15] = highlights;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = <div className="flex flex-col gap-4.5">{t8}</div>;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== button || $[20] !== price || $[21] !== priceUnderline || $[22] !== title) {
    t10 = price || priceUnderline || button?.link?.href ? <BillingDetails title={title} price={price} priceUnderline={priceUnderline} button={button} /> : null;
    $[19] = button;
    $[20] = price;
    $[21] = priceUnderline;
    $[22] = title;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t5 || $[26] !== t7 || $[27] !== t9) {
    t11 = <div className="grid lg:grid-cols-4 lg:gap-10 py-6.25 px-3.75 lg:p-10 border border-neutral-700 bg-brand-black">{t5}{t7}{t9}{t10}</div>;
    $[24] = t10;
    $[25] = t5;
    $[26] = t7;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t) {
    t12 = t("comparePlans");
    $[29] = t;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t12) {
    t13 = <Paragraph fontSize="bodyBoldLarge" className="text-neutral-light-100">{t12}</Paragraph>;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ArrowDown aria-hidden={true} className="size-6 shrink-0 mt-0 group-hover:mt-1 transition-[margin] duration-200" />;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t13) {
    t15 = <a href={`#${FEATURE_COMPARISON_ANCHOR_ID}`} className="group p-2 flex items-center justify-center gap-2.75 bg-surface-featured text-neutral-light-100 border-x border-b border-neutral-700 rounded-b-lg hover:bg-surface-featured/90 transition-colors cursor-pointer">{t13}{t14}</a>;
    $[34] = t13;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t11 || $[37] !== t15 || $[38] !== t2) {
    t16 = <Container paddingX={true} className="pb-12 lg:pb-10">{t2}{t11}{t15}</Container>;
    $[36] = t11;
    $[37] = t15;
    $[38] = t2;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return t16;
}
function _temp2(highlight_0) {
  return <div key={highlight_0.id} className="flex items-start"><HighlightIcon icon={highlight_0.icon} /><CkEditorCSRRenderer htmlContent={highlight_0.text} className={cn(styles["table-cell-content"], "text-neutral-light-100")} defaultTypography="bodyMedium" />{highlight_0.tooltip ? <Tooltip content={highlight_0.tooltip}><span><Info className="text-neutral-light-400 size-4 top-1 relative shrink-0 ml-1" /></span></Tooltip> : null}</div>;
}
function _temp(highlight) {
  return <div key={highlight.id} className="flex items-start"><HighlightIcon icon={highlight.icon} /><CkEditorCSRRenderer htmlContent={highlight.text} className={cn(styles["table-cell-content"], "text-neutral-light-100")} defaultTypography="bodyMedium" />{highlight.tooltip ? <Tooltip content={highlight.tooltip}><span><Info className="text-neutral-light-400 size-4 top-1 relative shrink-0 ml-1" /></span></Tooltip> : null}</div>;
}